// Wave 5c.2 — Inbox thread panel orchestrator (US-071..US-079).
//
// Owns the centre column of the inbox split-view: header → AI scoring banner
// → off-target callout → message history → mode-aware draft surface (auto
// preview / review editor / pending-action card / hot-override) → push-to-
// leads bar → mode-specific footer.
//
// Data flow:
//   - The conv + messages shapes come from props so 5c.1 can wire them
//     against `useConversation` / `useConversationMessages` once it merges.
//     For ad-hoc test rendering and visual regression we accept fixtures
//     directly.
//   - Outbound mutations (send, outcome, push-to-leads, hot-override)
//     route through 5c.2 hooks (`useSendMessage`, `useOutcomeReporter`,
//     `usePushToLeads`, `useHotOverride`).
//   - The AI draft body comes from `useDraftStream` in `auto`/`review`
//     mode; `useEffect` surfaces incremental deltas to the editor.
//
// US-079 manual-reply reconciliation: the parent caller subscribes to
// `message.in` events with `outboundSource === 'manual_linkedin'` via
// `useUserHubEvent`. When such an event lands, the parent invalidates the
// messages query (5c.1's hook handles the cache key) and passes the
// `manualReplyDetected` flag here so we pause auto-send for that thread.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useT } from '@barel/i18n';
import {
  useHotOverride as useHotOverrideHook,
  useOutcomeReporter,
  usePushToLeads as usePushToLeadsHook,
  useSendMessage,
  useUserHubChannel,
  type SendMessageInput,
} from '@barel/api-client/react';
import { Banner } from '@barel/ui';

import { toast } from '@/features/shell/GlobalToaster';

import { AIPauseOnSensitive } from './AIPauseOnSensitive';
import { AIScoringBanner } from './AIScoringBanner';
import { AiDraftSurface } from './AiDraftSurface';
import { AutoLeadDetectIndicator } from './AutoLeadDetectIndicator';
import { AutoPreview } from './AutoPreview';
import { Footer, type FooterMode } from './Footer';
import { HotOverride } from './HotOverride';
import { MessageBubbles } from './MessageBubbles';
import { OffTargetHandler } from './OffTargetHandler';
import { detectChannelShareInAiMessage } from './lib/modePolicy';
import { OutcomeConfirmation } from './OutcomeConfirmation';
import { PendingActionCard } from './PendingActionCard';
import { PushToLeadsBar } from './PushToLeadsBar';
import { ThreadHeader } from './ThreadHeader';
import { useDraftStream } from './useDraftStream';
import type { AiMode, ConversationView, ThreadMessage } from './types';

export interface ThreadPanelProps {
  conversation: ConversationView;
  messages: ThreadMessage[];
  /** Effective AI mode (account default + hot-override resolution). */
  aiMode: AiMode;
  /** True when user has enabled hot-override on top of an Auto account. */
  hotOverride?: boolean;
  /**
   * US-088 — mount the `<HotOverride>` explainer banner. The parent derives
   * this via `derivePolicy(...)` (auto account + very-hot conv +
   * `voice.notifyHotAction === 'validate'`); the actual mode swap to Review
   * is already carried by `hotOverride` / `aiMode`.
   */
  showHotOverride?: boolean;
  /**
   * US-087 — mount the `<AutoLeadDetectIndicator>` banner. The parent derives
   * this from `account.autoLeadDetect` + the server-side auto-capture marker.
   */
  autoLeadDetected?: boolean;
  /** True when the off-target mode should block AI replies (US-072). */
  offTargetBlocked?: boolean;
  /** True when the latest inbound carries `outboundSource === 'manual_linkedin'`. */
  manualReplyDetected?: boolean;
  /** Optional ContextPanel slot — 5c.3 owns the implementation. */
  contextPanel?: React.ReactNode;
  /** Nav handlers wired by the parent (router + 5d.2 leads tab). */
  onNavSettings?: () => void;
  onViewLeads?: () => void;
  /** Wave B3 — opens the billing/upgrade page when AI quota is exhausted. */
  onUpgrade?: () => void;
}

export function ThreadPanel({
  conversation,
  messages,
  aiMode,
  hotOverride = false,
  showHotOverride = false,
  autoLeadDetected = false,
  offTargetBlocked = false,
  manualReplyDetected = false,
  contextPanel,
  onNavSettings,
  onViewLeads,
  onUpgrade,
}: ThreadPanelProps) {
  const t = useT('inbox');

  const send = useSendMessage(conversation.id);
  const outcome = useOutcomeReporter(conversation.id);
  const pushToLeads = usePushToLeadsHook(conversation.id);
  const hotOverrideMutation = useHotOverrideHook(conversation.id);

  // Derived state — the prototype shows this as `pending`; we share the
  // boolean so the AI draft section is hidden when the conv is paused.
  const pending = !!conversation.pendingAction && !conversation.outcomeStatus;
  // Wave D5 (US-089) — channel-share lock keeps auto-mode paused until
  // the user reports the outcome of the share. Without this guard the FE
  // would still post the auto reply while the server-side `runDraftStream`
  // short-circuits, leading to a stale draft body racing through.
  const aiDisabled =
    pending || offTargetBlocked || manualReplyDetected || Boolean(conversation.channelShareLocked);

  // ─── Draft state ──────────────────────────────────────────────────────────
  const [draftBody, setDraftBody] = useState('');
  const [draftSent, setDraftSent] = useState(false);
  const [regenTrigger, setRegenTrigger] = useState(0);

  // Adversarial-review fix (2026-07-21) — server-side auto mode
  // (extIngest → AI_JOBS → auto-send, `apps/api/src/queue-consumers/aiJobs.ts`)
  // now drafts AND sends for `auto` accounts entirely on its own. The client
  // must NOT ALSO stream + auto-post a draft here, or a single inbound
  // message gets two outbound replies: this client's immediate send plus the
  // server's humanised-delay send. `pureAuto` gates every client-side
  // compose / auto-send code path below; hot-override demotes an auto
  // account back to the review-like "user must click send" flow, so it's
  // excluded here (unchanged behaviour).
  const pureAuto = aiMode === 'auto' && !hotOverride;

  // Auto-fetch the draft when the conv mounts in a non-pending, non-blocked,
  // non-pureAuto state. Review mode (and hot-override-on-auto) still stream
  // client-side and require an explicit user send.
  const draftStream = useDraftStream({
    conversationId: conversation.id,
    enabled: !aiDisabled && !pureAuto,
    trigger: regenTrigger,
    onComplete: (body) => setDraftBody(body),
  });

  // Keep the draft body in sync with the live SSE stream.
  useEffect(() => {
    if (draftStream.streaming) {
      setDraftBody(draftStream.body);
    }
  }, [draftStream.body, draftStream.streaming]);

  // ─── Server-driven auto-mode preview ───────────────────────────────────────
  // Pure-auto accounts never compose here, so the thread has nothing to show
  // between "inbound arrived" and "the persisted pending/sent/failed bubble
  // lands" (MessageBubbles, wired off `deliveryState`) unless we surface the
  // server's own draft. `draft.ready` fires for both auto and review
  // conversations the moment `runDraftStream` finishes
  // (`apps/api/src/services/ai/draftStream.ts`), so we use it purely as a
  // read-only preview — never as a trigger to send. Cleared once a
  // persisted `out` message with the same body shows up in `messages`; from
  // that point the delivery-truth bubble is the single source of truth and
  // keeping the preview mounted would duplicate it.
  const [autoDraftPreview, setAutoDraftPreview] = useState<{
    draftId: string;
    body: string;
  } | null>(null);

  useUserHubChannel('draft.ready', (event) => {
    if (!pureAuto) return;
    const payload = (('payload' in event ? event.payload : null) ?? {}) as {
      conversationId?: string;
      draftId?: string;
      body?: string;
    };
    if (
      payload.conversationId === conversation.id &&
      typeof payload.draftId === 'string' &&
      typeof payload.body === 'string'
    ) {
      setAutoDraftPreview({ draftId: payload.draftId, body: payload.body });
    }
  });

  const autoPreviewSuperseded = useMemo(() => {
    if (!autoDraftPreview) return false;
    return messages.some((m) => m.role === 'out' && m.body === autoDraftPreview.body);
  }, [messages, autoDraftPreview]);

  // Reset draft state on conv change.
  const sendTokenRef = useRef<{ body: string; token: string } | null>(null);
  useEffect(() => {
    setDraftBody('');
    setDraftSent(false);
    setAutoDraftPreview(null);
    sendTokenRef.current = null;
  }, [conversation.id]);

  // ─── Send ────────────────────────────────────────────────────────────────
  // SPEC-SEND-TRANSPORT §1.4 idempotency — a `clientToken` is generated once
  // per logical send attempt (keyed off the body being sent) and reused on
  // every retry of that SAME attempt (e.g. re-clicking "Envoyer" after a
  // failed toast), so a lost response can't double-send. Editing the draft
  // or regenerating produces new content, which naturally mints a fresh
  // token below.
  const onSend = useCallback(
    async (source: SendMessageInput['outboundSource'] = 'ai_review') => {
      if (!draftBody.trim()) return;
      if (sendTokenRef.current?.body !== draftBody) {
        sendTokenRef.current = { body: draftBody, token: crypto.randomUUID() };
      }
      try {
        await send.mutateAsync({
          body: draftBody,
          outboundSource: source,
          clientToken: sendTokenRef.current.token,
        });
        setDraftSent(true);
      } catch {
        toast.error({ title: t('thread.errors.sendFailed') });
      }
    },
    [draftBody, send, t],
  );

  // ─── Outcome ──────────────────────────────────────────────────────────────
  // Wave D5 (US-076) — extended outcome enum:
  //   - `client` → push-to-leads inline path (qualified lead).
  //   - `won` / `lost` → server marks the conv archived under that status.
  //   - `unknown` → server records the outcome but keeps the conv in queue.
  // Wave D5 (US-076) — `outcome_already_set` (HTTP 409) is a soft race that
  // happens when the user double-clicks the confirmation, or another tab
  // submitted the same outcome moments ago. We surface a softer info toast
  // instead of the generic outcomeFailed error.
  const onOutcomeSubmit = useCallback(
    async (status: 'followup' | 'archive' | 'client' | 'won' | 'lost' | 'unknown') => {
      try {
        if (status === 'client') {
          await pushToLeads.mutateAsync({ auto: false });
        } else {
          const result = await outcome.mutateAsync({ status });
          if (status === 'followup' && !result.relanceScheduled) {
            toast.error({ title: t('thread.errors.relanceScheduleFailed') });
          }
        }
      } catch (err) {
        const code =
          err && typeof err === 'object' && 'code' in err
            ? (err as { code?: unknown }).code
            : undefined;
        if (code === 'conversation.outcome_already_set') {
          toast.info({ title: t('thread.errors.outcomeAlreadySet') });
        } else {
          toast.error({ title: t('thread.errors.outcomeFailed') });
        }
      }
    },
    [outcome, pushToLeads, t],
  );

  // ─── Push to Leads ────────────────────────────────────────────────────────
  const onPushToLeads = useCallback(async () => {
    try {
      await pushToLeads.mutateAsync({ auto: false });
    } catch {
      toast.error({ title: t('thread.errors.pushFailed') });
    }
  }, [pushToLeads, t]);

  // ─── Hot override toggle (rendered in the header pill row) ────────────────
  const toggleHotOverride = useCallback(async () => {
    try {
      await hotOverrideMutation.mutateAsync({ enabled: !hotOverride });
    } catch {
      toast.error({ title: t('thread.errors.outcomeFailed') });
    }
  }, [hotOverride, hotOverrideMutation, t]);

  // ─── Footer mode resolution ───────────────────────────────────────────────
  const footerMode = useMemo<FooterMode>(() => {
    if (pending) return 'pending';
    if (hotOverride) return 'hot-override';
    if (aiMode === 'auto') return 'auto';
    return 'review';
  }, [pending, hotOverride, aiMode]);

  // ─── US-089 — channel-share pause tokens ──────────────────────────────────
  // The lock itself is server-persisted (`conversation.channelShareLocked`);
  // the token chips are re-derived client-side from the latest AI outbound
  // body via the shared detector so the banner can name what was shared.
  // Empty tokens are fine — the component falls back to the calendar label.
  const channelShareTokens = useMemo(() => {
    if (!conversation.channelShareLocked) return [];
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.role !== 'out') continue;
      if (m.outboundSource === 'ai_auto' || m.outboundSource === 'ai_review') {
        return detectChannelShareInAiMessage(m.body).tokens;
      }
    }
    return [];
  }, [conversation.channelShareLocked, messages]);

  return (
    <main
      data-ls-component="thread-panel"
      data-ai-mode={aiMode}
      data-pending={pending || undefined}
      data-hot-override={hotOverride || undefined}
      aria-label={String(conversation.contactName)}
      style={{
        display: 'flex',
        flexDirection: 'row',
        flex: '1 1 auto',
        minHeight: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minWidth: 0,
          background: 'var(--bg-base)',
          color: 'var(--text-primary)',
          fontFamily: 'var(--font-sans)',
          height: '100%',
          minHeight: 0,
        }}
      >
        {/* Header */}
        <ThreadHeader
          conversation={conversation}
          aiMode={aiMode}
          hotOverride={hotOverride}
          togglingHotOverride={hotOverrideMutation.isPending}
          onToggleHotOverride={() => {
            void toggleHotOverride();
          }}
        />

        {/* AI scoring banner */}
        <AIScoringBanner conv={conversation} />

        {/* US-088 — hot-lead override explainer (auto account + very hot +
            notifyHotAction=validate, derived by the parent via derivePolicy). */}
        {showHotOverride ? (
          <div style={{ padding: 'var(--space-3) var(--space-4) 0' }}>
            <HotOverride visible />
          </div>
        ) : null}

        {/* US-085 / US-086 — off-target banner (copy bifurcates on the
            per-account offTargetMode stance). */}
        {conversation.offTarget ? (
          <div style={{ padding: 'var(--space-3) var(--space-4) 0' }}>
            <OffTargetHandler
              visible
              offTargetMode={conversation.offTargetMode ?? 'block'}
              reason={conversation.offTargetReason}
              onOpenSettings={onNavSettings}
            />
          </div>
        ) : null}

        {/* US-087 — the AI auto-pushed this conv to Leads. */}
        {autoLeadDetected ? (
          <div style={{ padding: 'var(--space-3) var(--space-4) 0' }}>
            <AutoLeadDetectIndicator visible onOpenLeads={onViewLeads} />
          </div>
        ) : null}

        {/* History + draft surface */}
        <MessageBubbles
          conversationId={conversation.id}
          contactInitials={conversation.contactInitials}
          messages={messages}
          scrollKey={messages[messages.length - 1]?.id}
        />

        {/* US-089 — channel-share auto-pause. Renders next to the pending-
            action card (which is the outcome reporter that unlocks the AI). */}
        {conversation.channelShareLocked ? (
          <div style={{ padding: '0 var(--space-4) var(--space-3)' }}>
            <AIPauseOnSensitive visible tokens={channelShareTokens} />
          </div>
        ) : null}

        {/* Pending-action card */}
        {pending ? (
          <div style={{ padding: '0 var(--space-4) var(--space-3)' }}>
            <PendingActionCard
              conversation={conversation}
              onSubmit={onOutcomeSubmit}
              submitting={outcome.isPending || pushToLeads.isPending}
            />
          </div>
        ) : null}

        {/* US-076 — Green confirmation strip after the user reports an outcome. */}
        {conversation.pendingAction && conversation.outcomeStatus ? (
          <OutcomeConfirmation
            outcomeStatus={conversation.outcomeStatus}
            relanceScheduledAt={conversation.relanceScheduledAt}
          />
        ) : null}

        {/* US-079 — Manual-reply pause banner. */}
        {manualReplyDetected ? (
          <div
            style={{ padding: '0 var(--space-4) var(--space-3)' }}
            data-ls-region="manual-reply-pause"
          >
            <Banner tone="warning" title={t('ai.paused_manual_reply')}>
              {t('thread.footer.manualReplyHint')}
            </Banner>
          </div>
        ) : null}

        {/* Auto preview / Review editor — only when not pending + not blocked.
            Pure-auto accounts (no hot-override) never stream or send
            client-side (server-side auto mode owns that now) — we only show
            a read-only preview sourced from `draft.ready`, dropped as soon
            as the real delivery-truth bubble (MessageBubbles) supersedes it.
            Review mode (and hot-override-on-auto) keeps the editor mounted
            so the user can re-roll after a send. */}
        {pureAuto ? (
          !aiDisabled && autoDraftPreview && !autoPreviewSuperseded ? (
            <div style={{ padding: '0 var(--space-4) var(--space-3)' }}>
              <ol
                aria-label="ai draft surface"
                style={{ listStyle: 'none', margin: 0, padding: 0 }}
              >
                <AutoPreview body={autoDraftPreview.body} sent={false} />
              </ol>
            </div>
          ) : null
        ) : !aiDisabled && draftBody ? (
          <AiDraftSurface
            aiMode={aiMode}
            hotOverride={hotOverride}
            draftBody={draftBody}
            draftSent={draftSent}
            onChange={setDraftBody}
            onSend={() => onSend('ai_review')}
            onRegenerate={() => {
              setDraftSent(false);
              setRegenTrigger((v) => v + 1);
            }}
            streaming={draftStream.streaming}
            sending={send.isPending}
            serverError={draftStream.serverError}
            onUpgrade={onUpgrade ?? onNavSettings}
          />
        ) : null}

        {/* Push-to-Leads bar */}
        <PushToLeadsBar
          conversation={conversation}
          onPush={onPushToLeads}
          onViewLeads={onViewLeads}
          busy={pushToLeads.isPending}
        />

        {/* Footer */}
        <Footer
          mode={footerMode}
          pendingActionLabel={
            typeof conversation.pendingAction?.label === 'string'
              ? conversation.pendingAction.label
              : undefined
          }
          onNavSettings={onNavSettings}
        />
      </div>

      {/* Optional ContextPanel slot — 5c.3 owns the inner content. */}
      {contextPanel ? (
        <aside
          aria-label="lead context"
          style={{
            flex: '0 0 auto',
            borderLeft: '1px solid var(--border-subtle)',
            background: 'var(--bg-surface)',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {contextPanel}
        </aside>
      ) : null}
    </main>
  );
}

/** Re-exports so consumers can pull the sub-panels independently. */
export { AIScoringBanner } from './AIScoringBanner';
export { AutoPreview } from './AutoPreview';
export { Footer } from './Footer';
export { MessageBubbles } from './MessageBubbles';
export { PendingActionCard } from './PendingActionCard';
export { PushToLeadsBar } from './PushToLeadsBar';
export { ReviewEditor } from './ReviewEditor';
export { useDraftStream } from './useDraftStream';
export type { ConversationView, ThreadMessage, AiMode } from './types';
